import { useMemo, useState } from 'react'
import { ChevronLeft } from 'lucide-react'
import { Header } from '@components/Header.tsx'
import { Link, useRouter } from '@router'
import type { Activity, ActivityId } from '@hewey/shared'
import { ActivityCard } from './ActivityCard.tsx'
import { ActivityTagsDialog } from './ActivityTagsDialog.tsx'
import { byCreatedDesc } from './collections/shared.ts'
import styles from './UntaggedActivitiesPage.module.css'

type Props = {
	// Pending activities only — the parent does the filtering.
	activities: Activity[]
	loading: boolean
	onPatched: (activity: Activity) => void
}

// The backlog behind the landing page's callout: activities whose tagging was
// deferred, newest first. Tapping one opens the same tag panel the add flow
// ends on, so the list can be worked through without leaving the page.
export const UntaggedActivitiesPage = ({
	activities,
	loading,
	onPatched,
}: Props) => {
	const { navigate } = useRouter()
	const [selectedId, setSelectedId] = useState<ActivityId | null>(null)

	const sorted = useMemo(
		() => [...activities].sort(byCreatedDesc),
		[activities],
	)

	// Derived rather than held, so tapping a pill re-renders the dialog with
	// the updated activity. Marking one tagged drops it from `activities`,
	// which closes the dialog on its own.
	const selected = sorted.find((a) => a.id === selectedId) ?? null

	const backButton = (
		<button
			type="button"
			className={styles.back}
			onClick={() => navigate('/activities')}
			aria-label="Back to activities"
		>
			<ChevronLeft size={22} aria-hidden="true" />
		</button>
	)

	return (
		<main className={styles.screen}>
			<Header
				left={backButton}
				center={<h1 className={styles.title}>Needs tagging</h1>}
			/>

			{loading && activities.length === 0 ? (
				<p className={styles.empty}>Loading…</p>
			) : sorted.length === 0 ? (
				<p className={styles.empty}>
					Everything is tagged.
					<Link href="/activities" className={styles.emptyLink}>
						Back to activities
					</Link>
				</p>
			) : (
				<>
					<p className={styles.intro}>
						Tags decide which collections an activity appears in.
						Tap one to add its tags.
					</p>
					<ul className={styles.list}>
						{sorted.map((a) => (
							<li key={a.id}>
								<ActivityCard
									activity={a}
									compact
									onSelect={() => setSelectedId(a.id)}
								/>
							</li>
						))}
					</ul>
				</>
			)}

			{selected ? (
				<ActivityTagsDialog
					open
					activity={selected}
					onPatched={onPatched}
					onDone={() => setSelectedId(null)}
					onClose={() => setSelectedId(null)}
				/>
			) : null}
		</main>
	)
}
